import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus, Search, Share2, Stethoscope, Phone, Mail, MoreVertical,
  User as UserIcon, ChevronLeft, ChevronRight, Filter, Eye, Pencil,
  CalendarDays, ClipboardList, Receipt, Trash2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import ShareDocumentsModal from "@/components/ShareDocumentsModal";
import DuplicateAlertModal from "@/components/DuplicateAlertModal";
import PatientAutocomplete from "@/components/PatientAutocomplete";
import PatientAvatar from "@/components/PatientAvatar";
import AssignDoctorDialog from "@/components/AssignDoctorDialog";
import { DateDMYInput } from "@/components/DateDMYInput";

// Whole-year age from a DOB string (accepts ISO YYYY-MM-DD or dd/mm/yyyy —
// falls through to `Date` parsing). Returns null when the DOB is missing,
// unparseable, in the future, or outside the 0-150 sanity range. Keep this
// in sync with `_compute_age_from_dob` on the backend — server always wins.
const computeAgeYears = (dob) => {
  if (!dob) return null;
  let d;
  if (typeof dob === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
    const [dd, mm, yy] = dob.split("/").map((s) => parseInt(s, 10));
    d = new Date(yy, mm - 1, dd);
  } else {
    d = new Date(dob);
  }
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  if (d > now) return null;
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
  if (years < 0 || years > 150) return null;
  return years;
};

// ---- helpers ----------------------------------------------------------------
const PAGE_SIZE = 10;

// Prefix a doctor's display name with "Dr." unless it already starts with one.
const withDrPrefix = (name) => {
  if (!name) return "";
  return /^dr\.?\s+/i.test(name) ? name : `Dr. ${name}`;
};

// Soft, distinct badge tone per allergy keyword — subtle, not clinical.
const ALLERGY_TONES = {
  none:       { bg: "#EDEEEE", fg: "#5B6660" },
  penicillin: { bg: "#F6E3E5", fg: "#7A2A2E" },
  peanuts:    { bg: "#F6E3E5", fg: "#7A2A2E" },
  dust:       { bg: "#FDF3ED", fg: "#8A3F1E" },
  latex:      { bg: "#F5EFE0", fg: "#7A5A20" },
  pollen:     { bg: "#E0EBDE", fg: "#264029" },
  aspirin:    { bg: "#F0E7EE", fg: "#4A2148" },
};
const allergyTone = (kw = "") => {
  const key = kw.trim().toLowerCase();
  if (!key || key === "none" || key === "n/a") return ALLERGY_TONES.none;
  return ALLERGY_TONES[key] || { bg: "#E4EFF5", fg: "#1F4B66" };
};

// ---- micro-components -------------------------------------------------------
// (PatientAvatar lives in `components/PatientAvatar.jsx` — the SINGLE
// reusable component. Do not re-implement initial rendering here.)

function DoctorBadge({ doctorName, canAssign, onAssign, userRole }) {
  const unassigned = !doctorName || doctorName === "Unassigned";
  if (unassigned && canAssign) {
    const label = userRole === "doctor" ? "Assign to Me" : "Assign Doctor";
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAssign?.(); }}
        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full mt-1 bg-[#FDF3ED] text-[#8A3F1E] border border-[#EAC5AE] hover:bg-[#F9E4D3] transition"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-clay" />
        {label}
      </button>
    );
  }
  const Icon = unassigned ? UserIcon : Stethoscope;
  const clickable = canAssign && !unassigned;
  const cls = `inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full mt-1 ${
    unassigned ? "bg-[#EDEEEE] text-ink-soft italic" : "bg-linen/70 text-ink-mid"
  } ${clickable ? "cursor-pointer hover:ring-1 hover:ring-pine/30" : ""}`;
  if (clickable) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onAssign?.(); }}
        className={cls}
        title="Change treating doctor"
      >
        <Icon className="w-3 h-3 opacity-80" />
        {doctorName}
      </button>
    );
  }
  return (
    <span className={cls}>
      <Icon className="w-3 h-3 opacity-80" />
      {unassigned ? "Unassigned" : doctorName}
    </span>
  );
}

function AllergyPills({ allergies }) {
  const raw = (allergies || "").trim();
  if (!raw || /^(none|n\/a|-|—)$/i.test(raw)) {
    const t = ALLERGY_TONES.none;
    return (
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px]"
        style={{ background: t.bg, color: t.fg }}
      >
        None
      </span>
    );
  }
  const parts = raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean).slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1.5">
      {parts.map((p, i) => {
        const t = allergyTone(p);
        return (
          <span key={`${p}-${i}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] whitespace-nowrap"
                style={{ background: t.bg, color: t.fg }}>
            {p}
          </span>
        );
      })}
    </div>
  );
}

function PatientRowActions({ patient, onShare, onDelete }) {
  const navigate = useNavigate();
  const go = (path) => () => navigate(path);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`patient-actions-${patient.id}`}
          onClick={(e) => e.stopPropagation()}
          className="w-9 h-9 rounded-full grid place-items-center text-ink-mid hover:bg-linen hover:text-ink transition"
          aria-label="Row actions"
          title="Row actions"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 bg-white border-[#E8EAE9]">
        <DropdownMenuItem onClick={go(`/app/patients/${patient.id}`)} data-testid={`row-view-${patient.id}`}>
          <Eye className="w-4 h-4 mr-2 text-ink-mid" /> View Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={go(`/app/patients/${patient.id}?edit=1`)} data-testid={`row-edit-${patient.id}`}>
          <Pencil className="w-4 h-4 mr-2 text-ink-mid" /> Edit Patient
        </DropdownMenuItem>
        <DropdownMenuItem onClick={go(`/app/calendar?patient_id=${patient.id}`)} data-testid={`row-appts-${patient.id}`}>
          <CalendarDays className="w-4 h-4 mr-2 text-ink-mid" /> Appointments
        </DropdownMenuItem>
        <DropdownMenuItem onClick={go(`/app/patients/${patient.id}?tab=treatments`)} data-testid={`row-treatments-${patient.id}`}>
          <ClipboardList className="w-4 h-4 mr-2 text-ink-mid" /> Treatments
        </DropdownMenuItem>
        <DropdownMenuItem onClick={go(`/app/billing?patient_id=${patient.id}`)} data-testid={`row-billing-${patient.id}`}>
          <Receipt className="w-4 h-4 mr-2 text-ink-mid" /> Billing
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onShare(patient)} data-testid={`row-share-${patient.id}`}>
          <Share2 className="w-4 h-4 mr-2 text-ink-mid" /> Share Documents
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onDelete(patient)}
          data-testid={`row-delete-${patient.id}`}
          className="text-[#B94A48] focus:text-[#B94A48] focus:bg-[#FCECEC]"
        >
          <Trash2 className="w-4 h-4 mr-2" /> Delete Patient
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---- Page -------------------------------------------------------------------
export default function Patients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role;
  const showDoctorMeta = true;                       // all roles see the primary doctor
  const showDoctorFilter = role !== "doctor";        // doctor login hides the doctor filter
  const canBulkAssign = role === "admin" || role === "receptionist";
  const [patients, setPatients] = useState([]);
  const [team, setTeam] = useState([]);
  const [q, setQ] = useState("");
  const [doctorFilter, setDoctorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // future-ready
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [sharePatientId, setSharePatientId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", dob: "", age: "", gender: "", allergies: "", address: "", assigned_doctor_id: "", clinic_patient_id: "" });
  const [dupeMatches, setDupeMatches] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Doctor-assignment dialog (single + bulk modes)
  const [assignFor, setAssignFor] = useState(null);   // { id, name, assigned_doctor_id }
  const [bulkAssignIds, setBulkAssignIds] = useState(null); // string[] | null

  const load = useCallback(async () => {
    const [{ data: p }, teamRes] = await Promise.all([
      api.get("/patients"),
      api.get("/team").catch(() => ({ data: [] })),
    ]);
    setPatients(p);
    setTeam(teamRes.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const doctorMap = useMemo(() => {
    const out = {};
    for (const t of team) {
      if (t.role === "doctor" || t.role === "admin") {
        out[t.id] = withDrPrefix(t.name || "Doctor");
      }
    }
    return out;
  }, [team]);
  const doctorOptions = useMemo(
    () => Object.entries(doctorMap).map(([id, name]) => ({ id, name })),
    [doctorMap],
  );
  const treatingDoctorName = (p) =>
    p.assigned_doctor_id ? (doctorMap[p.assigned_doctor_id] || "Unassigned") : "Unassigned";

  const create = async (e, { force = false } = {}) => {
    e?.preventDefault?.();
    if (submitting) return;
    setSubmitting(true);
    try {
      // Send `assigned_doctor_id` only when non-empty — the backend Pydantic
      // model accepts null for unassigned, and empty-string would fail validation.
      const payload = { ...form };
      if (!payload.assigned_doctor_id) delete payload.assigned_doctor_id;
      // Normalise age → integer or null (backend also validates 0-150).
      if (payload.age === "" || payload.age === undefined) {
        delete payload.age;
      } else {
        const n = parseInt(payload.age, 10);
        payload.age = Number.isNaN(n) ? null : n;
      }
      await api.post(force ? "/patients?force=true" : "/patients", payload);
      toast.success("Patient added");
      setOpen(false);
      setDupeMatches(null);
      setForm({ name: "", email: "", phone: "", dob: "", age: "", gender: "", allergies: "", address: "", assigned_doctor_id: "", clinic_patient_id: "" });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === "duplicate_patient") {
        setDupeMatches(detail.matches || []);
      } else if (err?.response?.status === 409 && detail?.error === "duplicate_clinic_patient_id") {
        toast.error(`Patient ID already used by ${detail.existing_patient_name || "another patient"}`);
      } else {
        toast.error(detail?.message || "Failed to create");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const removePatient = async (p) => {
    if (!window.confirm(`Delete ${p.name}? This action cannot be undone.`)) return;
    try {
      await api.delete(`/patients/${p.id}`);
      toast.success("Patient deleted");
      load();
    } catch (err) {
      toast.error(apiError(err, "Delete failed"));
    }
  };

  const filtered = useMemo(() => patients.filter((p) => {
    if (showDoctorMeta && doctorFilter !== "all") {
      if (doctorFilter === "unassigned") {
        if (p.assigned_doctor_id) return false;
      } else if (p.assigned_doctor_id !== doctorFilter) {
        return false;
      }
    }
    // statusFilter reserved for future — allow "all" through until statuses ship.
    const query = q.trim().toLowerCase();
    if (!query) return true;
    const doctorName = showDoctorMeta ? treatingDoctorName(p).toLowerCase() : "";
    return (
      (p.name || "").toLowerCase().includes(query) ||
      (p.phone || "").toLowerCase().includes(query) ||
      (p.email || "").toLowerCase().includes(query) ||
      (p.allergies || "").toLowerCase().includes(query) ||
      (p.clinic_patient_id || "").toLowerCase().includes(query) ||
      (showDoctorMeta && doctorName.includes(query))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [patients, q, doctorFilter, statusFilter, showDoctorMeta, doctorMap]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [q, doctorFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageStart = (page - 1) * PAGE_SIZE;
  const paged = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  const sendRecord = (p) => {
    if (!p.phone && !p.email) return toast.error("Patient has no phone or email on file");
    setSharePatientId(p.id);
  };

  return (
    <div>
      {/* ---- Header ------------------------------------------------------- */}
      <div className="flex items-end justify-between mb-8">
        <div>
          <span className="overline text-ink-soft">Patients</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Everyone in your care.</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="btn-add-patient" className="btn-primary text-sm inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> New patient
            </button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-display text-2xl">Add patient</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3" data-testid="new-patient-form">
              <div>
                <input required data-testid="patient-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
                <PatientAutocomplete value={form.name} onPickExisting={(p) => { setOpen(false); navigate(`/app/patients/${p.id}`); }} />
              </div>
              <div>
                <input required data-testid="patient-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
                <PatientAutocomplete value={form.phone} onPickExisting={(p) => { setOpen(false); navigate(`/app/patients/${p.id}`); }} />
              </div>
              <input data-testid="patient-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
              <div>
                <input
                  data-testid="patient-clinic-id"
                  placeholder="Patient ID (optional, e.g. PT-2026-0042)"
                  value={form.clinic_patient_id}
                  onChange={(e) => setForm({ ...form, clinic_patient_id: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40 font-mono"
                />
                <div className="text-[10px] text-ink-soft mt-1 px-1">Your clinic's own identifier — must be unique within the clinic. Leave blank to skip.</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <DateDMYInput
                  data-testid="patient-dob"
                  placeholder="DOB"
                  value={form.dob}
                  onChange={(e) => {
                    const dob = e.target.value;
                    // Auto-populate age when DOB parses to a real date.
                    const derived = computeAgeYears(dob);
                    setForm((prev) => ({
                      ...prev,
                      dob,
                      age: derived !== null ? String(derived) : prev.age,
                    }));
                  }}
                  className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40"
                />
                <input
                  data-testid="patient-age"
                  type="number"
                  min={0}
                  max={150}
                  step={1}
                  inputMode="numeric"
                  placeholder="Age (years)"
                  value={form.age}
                  onChange={(e) => {
                    // Whole numbers only, clamp to 0-150. Blank stays blank.
                    const raw = e.target.value.replace(/[^0-9]/g, "");
                    if (!raw) return setForm((prev) => ({ ...prev, age: "" }));
                    const n = Math.min(150, Math.max(0, parseInt(raw, 10)));
                    setForm((prev) => ({ ...prev, age: String(n) }));
                  }}
                  className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40"
                />
              </div>
              <select data-testid="patient-gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40 bg-white">
                <option value="">Gender</option><option>F</option><option>M</option><option>Other</option>
              </select>
              <input data-testid="patient-allergies" placeholder="Allergies" value={form.allergies} onChange={(e) => setForm({ ...form, allergies: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
              <textarea data-testid="patient-address" placeholder="Address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40 resize-none" />
              {(role === "admin" || role === "receptionist") && (
                <div className="rounded-xl border border-[#E8EAE9] bg-white px-4 py-2.5">
                  <div className="text-[10px] uppercase tracking-widest text-ink-soft mb-1">
                    Primary treating doctor <span className="opacity-70 normal-case tracking-normal">(optional)</span>
                  </div>
                  <select
                    data-testid="patient-primary-doctor"
                    value={form.assigned_doctor_id}
                    onChange={(e) => setForm({ ...form, assigned_doctor_id: e.target.value })}
                    className="w-full text-sm bg-transparent focus:outline-none cursor-pointer"
                  >
                    <option value="">Unassigned</option>
                    {doctorOptions.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <button data-testid="patient-submit" className="w-full btn-primary text-sm">Add patient</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* ---- Card wrapper (filters + table + pagination) ----------------- */}
      <div className="card-widget">
        {/* Filters row */}
        <div className="flex flex-col md:flex-row items-stretch gap-3 mb-5" data-testid="patients-toolbar">
          <div className="flex-1 flex items-center gap-3 border border-[#E8EAE9] rounded-xl px-4 py-3 focus-within:border-pine/40 transition bg-white"
               data-testid="patients-search-wrap">
            <Search className="w-5 h-5 text-ink-mid flex-shrink-0" />
            <input
              data-testid="patients-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={showDoctorMeta
                ? "Search patients by name, phone, email, allergy, or doctor…"
                : "Search patients by name, phone, email, or allergy…"}
              className="bg-transparent text-sm w-full focus:outline-none placeholder:text-ink-soft"
              autoFocus
            />
            {q && (
              <span data-testid="patients-search-count" className="text-xs text-ink-mid whitespace-nowrap">
                {filtered.length} match{filtered.length === 1 ? "" : "es"}
              </span>
            )}
          </div>
          {showDoctorFilter && (
            <div className="flex items-center gap-2 border border-[#E8EAE9] rounded-xl px-3.5 py-2.5 bg-white shrink-0 min-w-[180px]"
                 data-testid="patients-doctor-filter-wrap">
              <Stethoscope className="w-4 h-4 text-ink-mid" />
              <select
                data-testid="patients-doctor-filter"
                value={doctorFilter}
                onChange={(e) => setDoctorFilter(e.target.value)}
                className="bg-transparent text-sm focus:outline-none pr-2 flex-1 truncate cursor-pointer"
                aria-label="Filter by treating doctor"
              >
                <option value="all">All doctors</option>
                <option value="unassigned">Unassigned</option>
                {doctorOptions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
          )}
          {canBulkAssign && (
            (() => {
              const eligible = filtered.filter((p) => !p.assigned_doctor_id);
              if (eligible.length < 2) return null;
              return (
                <button
                  type="button"
                  onClick={() => setBulkAssignIds(eligible.map((p) => p.id))}
                  data-testid="patients-bulk-assign"
                  className="rounded-xl border border-clay/30 bg-[#FDF3ED] text-[#8A3F1E] px-3.5 py-2.5 text-sm inline-flex items-center gap-2 hover:bg-[#F9E4D3] transition shrink-0"
                  title={`Assign a treating doctor to ${eligible.length} unassigned patient${eligible.length === 1 ? "" : "s"}`}
                >
                  <Stethoscope className="w-4 h-4" />
                  Bulk assign
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-clay text-linen">{eligible.length}</span>
                </button>
              );
            })()
          )}
          <div className="flex items-center gap-2 border border-[#E8EAE9] rounded-xl px-3.5 py-2.5 bg-white shrink-0 min-w-[160px]"
               data-testid="patients-status-filter-wrap"
               title="Patient status — coming soon">
            <Filter className="w-4 h-4 text-ink-mid" />
            <select
              data-testid="patients-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent text-sm focus:outline-none pr-2 flex-1 truncate cursor-pointer"
              aria-label="Filter by status"
            >
              <option value="all">All status</option>
              <option value="active" disabled>Active (soon)</option>
              <option value="inactive" disabled>Inactive (soon)</option>
              <option value="archived" disabled>Archived (soon)</option>
            </select>
          </div>
        </div>

        {/* Desktop / tablet table -------------------------------------- */}
        <div className="hidden md:block">
          <div className="grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1fr)_72px] gap-4 px-4 py-2 overline text-ink-soft border-b border-[#E8EAE9]">
            <div>Patient</div>
            <div>Contact</div>
            <div>Allergies</div>
            <div className="text-right pr-1">Actions</div>
          </div>
          <div className="divide-y divide-[#E8EAE9]">
            {paged.map((p) => (
              <div
                key={p.id}
                data-testid={`patient-row-${p.id}`}
                className="grid grid-cols-[minmax(0,2.2fr)_minmax(0,1.6fr)_minmax(0,1fr)_72px] gap-4 px-4 py-4 items-center hover:bg-linen/40 transition rounded-lg"
              >
                {/* Patient column */}
                <div className="flex items-start gap-3 min-w-0">
                  <PatientAvatar
                    patient={p}
                    size="md"
                    clickable
                    onClick={() => navigate(`/app/patients/${p.id}`)}
                  />
                  <div className="min-w-0">
                    <Link
                      to={`/app/patients/${p.id}`}
                      className="font-medium text-ink hover:underline block truncate"
                      data-testid={`patient-name-${p.id}`}
                    >
                      {p.name}
                    </Link>
                    <div className="text-[10px] font-mono text-ink-soft tracking-wide" data-testid={`patient-cpid-${p.id}`}>
                      {p.clinic_patient_id || "—"}
                    </div>
                    <div data-testid={`patient-doctor-${p.id}`}>
                      <DoctorBadge
                        doctorName={treatingDoctorName(p)}
                        userRole={role}
                        canAssign={
                          role === "admin" || role === "receptionist"
                            ? true
                            : (role === "doctor" && !p.assigned_doctor_id)  // doctor: only self-claim unassigned
                        }
                        onAssign={() => setAssignFor({
                          id: p.id, name: p.name, assigned_doctor_id: p.assigned_doctor_id || null,
                        })}
                      />
                    </div>
                  </div>
                </div>

                {/* Contact column */}
                <div className="min-w-0 space-y-1" data-testid={`patient-contact-${p.id}`}>
                  <div className="flex items-center gap-2 text-sm text-ink-mid truncate">
                    <Phone className="w-3.5 h-3.5 text-pine shrink-0" />
                    <span className="truncate">{p.phone || "—"}</span>
                  </div>
                  {p.email && (
                    <div className="flex items-center gap-2 text-xs text-ink-soft truncate">
                      <Mail className="w-3.5 h-3.5 text-ink-mid shrink-0" />
                      <span className="truncate">{p.email}</span>
                    </div>
                  )}
                </div>

                {/* Allergies column */}
                <div className="min-w-0" data-testid={`patient-allergies-${p.id}`}>
                  <AllergyPills allergies={p.allergies} />
                </div>

                {/* Actions column */}
                <div className="flex justify-end">
                  <PatientRowActions patient={p} onShare={sendRecord} onDelete={removePatient} />
                </div>
              </div>
            ))}
            {paged.length === 0 && (
              <div className="py-12 text-center text-ink-soft" data-testid="patients-empty">
                {filtered.length === 0 && q ? "No patients match your search." : "No patients yet."}
              </div>
            )}
          </div>
        </div>

        {/* Mobile card list -------------------------------------------- */}
        <div className="md:hidden space-y-3">
          {paged.map((p) => (
            <div
              key={p.id}
              data-testid={`patient-card-${p.id}`}
              className="rounded-2xl border border-[#E8EAE9] bg-white p-4 hover:shadow-sm transition"
            >
              <div className="flex items-start gap-3">
                <PatientAvatar
                  patient={p}
                  size="md"
                  clickable
                  onClick={() => navigate(`/app/patients/${p.id}`)}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/app/patients/${p.id}`} className="font-medium text-ink hover:underline truncate">
                      {p.name}
                    </Link>
                    <PatientRowActions patient={p} onShare={sendRecord} onDelete={removePatient} />
                  </div>
                  <DoctorBadge
                    doctorName={treatingDoctorName(p)}
                    userRole={role}
                    canAssign={
                      role === "admin" || role === "receptionist"
                        ? true
                        : (role === "doctor" && !p.assigned_doctor_id)
                    }
                    onAssign={() => setAssignFor({
                      id: p.id, name: p.name, assigned_doctor_id: p.assigned_doctor_id || null,
                    })}
                  />
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2 text-sm text-ink-mid">
                      <Phone className="w-3.5 h-3.5 text-pine shrink-0" /> <span className="truncate">{p.phone || "—"}</span>
                    </div>
                    {p.email && (
                      <div className="flex items-center gap-2 text-xs text-ink-soft">
                        <Mail className="w-3.5 h-3.5 text-ink-mid shrink-0" /> <span className="truncate">{p.email}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3"><AllergyPills allergies={p.allergies} /></div>
                </div>
              </div>
            </div>
          ))}
          {paged.length === 0 && (
            <div className="py-10 text-center text-ink-soft text-sm">No patients yet.</div>
          )}
        </div>

        {/* Pagination -------------------------------------------------- */}
        {filtered.length > 0 && (
          <div className="mt-5 flex flex-col-reverse sm:flex-row items-center justify-between gap-3 pt-4 border-t border-[#E8EAE9]"
               data-testid="patients-pagination">
            <div className="text-xs text-ink-soft">
              Showing <span className="text-ink">{pageStart + 1}</span> to <span className="text-ink">{Math.min(pageStart + PAGE_SIZE, filtered.length)}</span> of <span className="text-ink">{filtered.length}</span> patients
            </div>
            <PagerControls page={page} totalPages={totalPages} onChange={setPage} />
          </div>
        )}
      </div>

      {sharePatientId && (
        <ShareDocumentsModal
          patientId={sharePatientId}
          onClose={() => setSharePatientId(null)}
        />
      )}
      <DuplicateAlertModal
        open={!!dupeMatches}
        matches={dupeMatches || []}
        attempted={form}
        onCancel={() => setDupeMatches(null)}
        onForceCreate={() => create(null, { force: true })}
        onOpenExisting={() => { setOpen(false); setDupeMatches(null); }}
      />
      <AssignDoctorDialog
        open={!!assignFor}
        patient={assignFor}
        team={team}
        currentUser={user}
        onClose={() => setAssignFor(null)}
        onSaved={(newDoctorId) => {
          setPatients((list) => list.map((x) =>
            x.id === assignFor?.id ? { ...x, assigned_doctor_id: newDoctorId } : x
          ));
          setAssignFor(null);
        }}
      />
      <AssignDoctorDialog
        open={!!bulkAssignIds}
        bulkPatientIds={bulkAssignIds || []}
        team={team}
        currentUser={user}
        onClose={() => setBulkAssignIds(null)}
        onSaved={(newDoctorId) => {
          const ids = new Set(bulkAssignIds || []);
          setPatients((list) => list.map((x) =>
            ids.has(x.id) ? { ...x, assigned_doctor_id: newDoctorId } : x
          ));
          setBulkAssignIds(null);
        }}
      />
    </div>
  );
}

// ---- Pagination widget (compact, mimics the reference) ----------------------
function PagerControls({ page, totalPages, onChange }) {
  const numbers = useMemo(() => {
    // Compact 1-based sequence with ellipses, capped at 5 visible page-tokens.
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, page, page - 1, page + 1]);
    const clean = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < clean.length; i++) {
      if (i > 0 && clean[i] - clean[i - 1] > 1) out.push("…");
      out.push(clean[i]);
    }
    return out;
  }, [page, totalPages]);

  const btn = "w-8 h-8 rounded-lg text-xs grid place-items-center border border-[#E8EAE9] bg-white hover:bg-linen/60 text-ink transition";
  return (
    <div className="flex items-center gap-1.5">
      <button
        data-testid="pager-prev"
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
        className={`${btn} disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Previous page"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      {numbers.map((n, i) =>
        n === "…" ? (
          <span key={`e-${i}`} className="w-8 h-8 grid place-items-center text-xs text-ink-soft">…</span>
        ) : (
          <button
            key={n}
            data-testid={`pager-${n}`}
            onClick={() => onChange(n)}
            className={`${btn} ${
              n === page ? "!bg-clay !text-linen !border-clay" : ""
            }`}
            aria-current={n === page ? "page" : undefined}
          >
            {n}
          </button>
        )
      )}
      <button
        data-testid="pager-next"
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
        className={`${btn} disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Next page"
      >
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
